import { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  CustomerDocumentRequest,
  CustomerDocumentsQuery,
  PaginationOptions,
} from "lib/generated/app-service-gql/graphql";
import { CustomerDocumentSortBy, CustomerDocumentSortOrder } from "lib/types/graphQLEnums";
import { useGraphQlQuery } from "../useGraphQlClient";

export type CustomerDocumentSortState = {
  field: CustomerDocumentSortBy;
  order: CustomerDocumentSortOrder;
};

type Filters = {
  pagination: PaginationOptions;
  sortState: CustomerDocumentSortState;
  // search?: string;
};

export function customerDocumentsKey({
  documentType,
  resourceId,
  uiPath,
  filters,
}: {
  documentType: string;
  resourceId: string | null | undefined;
  uiPath: string | null | undefined;
  filters?: Filters;
}): QueryKey {
  const queryKey = [
    "customerDocuments",
    documentType,
    // Normalize these optional fields to null
    resourceId ?? null,
    uiPath ?? null,
  ];

  if (filters) {
    queryKey.push(JSON.stringify(filters));
  }

  return queryKey;
}

const CUSTOMER_DOCUMENTS_QUERY = graphql(`
  query customerDocuments($request: CustomerDocumentRequest!, $pagination: PaginationOptions!) {
    customerDocuments(CustomerDocumentRequest: $request, PaginationOptions: $pagination) {
      items {
        id
        fileName
        description
        uiPath
        isFolder
        createdAt
        updatedAt
      }
      totalCount
    }
  }
`);

export type CustomerDocumentDetails = CustomerDocumentsQuery["customerDocuments"]["items"][0];

export function useCustomerDocuments(
  request: CustomerDocumentRequest,
  pagination: PaginationOptions,
  options?: UseQueryOptions<CustomerDocumentsQuery, unknown, CustomerDocumentsQuery, QueryKey>,
) {
  const { documentType, resourceId, uiPath } = request;
  const { data, ...rest } = useGraphQlQuery(
    customerDocumentsKey({
      documentType,
      resourceId,
      uiPath,
      filters: {
        pagination,
        sortState: {
          field: request.sortBy,
          order: request.sortOrder,
        },
      },
    }),
    CUSTOMER_DOCUMENTS_QUERY,
    [{ request, pagination }],
    {
      ...options,
    },
  );

  return {
    data: data?.customerDocuments.items || [],
    totalCount: data?.customerDocuments.totalCount || 0,
    ...rest,
  };
}
